import { CpsAccordion, CpsButtonText, CpsCheckbox } from "corpus";
import type { ChangeEvent } from "react";
import { useRouter, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { type FragmentType, graphql, useFragment } from "@repo/graphql-types";
import { DrawerBody, DrawerContent, DrawerFooter, DrawerRoot } from "@/components/drawer";
import {
  FormControl,
  FormField,
  FormHandlerSubmit,
  FormItem,
  FormRoot,
  FormSubmitButton,
} from "@/components/form";

export interface CommunityFilterDrawerParams {
  action?: string;
  locations?: string[];
  specialties?: string[];
}

interface FilterForm {
  locations: string[];
  specialties: string[];
}

export const CommunityFilterDrawerQueryFragment = graphql(/* GraphQL */ `
  fragment CommunityFilterDrawerQueryFragment on query_root {
    locations: unidades(
      where: { codUnidade: { _nin: [1, 6] } }
      order_by: { nome: asc }
    ) {
      codUnidade
      nome
    }

    specialties: especialidades(order_by: { nome: asc }) {
      nome
      codEspecialidade
    }
  }
`);

interface CommunityFilterDrawerProps {
  data: FragmentType<typeof CommunityFilterDrawerQueryFragment>;
}
export const CommunityFilterDrawer = ({
  data,
}: CommunityFilterDrawerProps): JSX.Element => {
  const params: CommunityFilterDrawerParams = useSearch({ from: "/settings/community/" });
  const router = useRouter();

  const form = useForm<FilterForm>({
    resolver: zodResolver(
      z.object({
        locations: z.array(z.string()),
        specialties: z.array(z.string()),
      }),
    ),
    defaultValues: {
      locations: [],
      specialties: [],
    },
  });

  const { locations, specialties } = useFragment(
    CommunityFilterDrawerQueryFragment,
    data,
  );

  const onChangeSpecialties = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    const selectedSpec = form.getValues("specialties");
    if (selectedSpec.includes(value)) {
      form.setValue(
        "specialties",
        selectedSpec.filter((specialty) => specialty !== value),
      );
    } else {
      form.setValue("specialties", [...selectedSpec, value]);
    }
  };

  const onChangeLocations = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    const selectedLoc = form.getValues("locations");
    if (selectedLoc.includes(value)) {
      form.setValue(
        "locations",
        selectedLoc.filter((location) => location !== value),
      );
    } else {
      form.setValue("locations", [...selectedLoc, value]);
    }
  };

  const setDrawerDismiss = (value: boolean): void => {
    if (!value && params.action === "filter-drawer") {
      void router.navigate({
        search: { ...params, action: undefined },
        replace: true,
      });
    }
  };

  const handleSubmit = (): void => {
    void router.navigate({
      search: {
        ...params,
        action: undefined,
        locations:
          form.getValues("locations").length > 0
            ? form.getValues("locations")
            : undefined,
        specialties:
          form.getValues("specialties").length > 0
            ? form.getValues("specialties")
            : undefined,
      },
      replace: true,
    });
  };

  const resetFilterButton = (): void => {
    form.setValue("locations", []);
    form.setValue("specialties", []);
    void router.navigate({
      search: {
        ...params,
        action: undefined,
        locations: undefined,
        specialties: undefined,
      },
      replace: true,
    });
  };

  return (
    <DrawerRoot open={params.action === "filter-drawer"} setOpen={setDrawerDismiss}>
      <FormRoot {...form}>
        <DrawerContent>
          <FormHandlerSubmit handleSubmit={handleSubmit} className="w-full">
            <DrawerBody>
              <div className="flex flex-col gap-4 my-6 ">
                <CpsAccordion title="Especialidades">
                  <div className="flex flex-col gap-y-2 pb-4">
                    <FormField
                      control={form.control}
                      name="specialties"
                      render={() => (
                        <FormItem>
                          <FormControl>
                            <div className="gap-1 flex flex-col">
                              {specialties.map((specialty) => (
                                <CpsCheckbox
                                  title={String(specialty.nome)}
                                  value={String(specialty.codEspecialidade)}
                                  id={`specialty-${specialty.codEspecialidade}-check`}
                                  key={`specialty-${specialty.codEspecialidade}`}
                                  color="primary"
                                  checked={form
                                    .getValues("specialties")
                                    .includes(String(specialty.codEspecialidade))}
                                  onChange={onChangeSpecialties}
                                />
                              ))}
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </CpsAccordion>
                <CpsAccordion title="Unidades">
                  <div className="flex flex-col gap-y-2 pb-4">
                    <FormField
                      control={form.control}
                      name="locations"
                      render={() => (
                        <FormItem>
                          <FormControl>
                            <div className="gap-1 flex flex-col">
                              {locations.map((location) => (
                                <CpsCheckbox
                                  title={String(location.nome)}
                                  value={String(location.codUnidade)}
                                  id={`location-${location.codUnidade}-check`}
                                  key={`location-${location.codUnidade}`}
                                  color="primary"
                                  checked={form
                                    .getValues("locations")
                                    .includes(String(location.codUnidade))}
                                  onChange={onChangeLocations}
                                />
                              ))}
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                </CpsAccordion>
              </div>
            </DrawerBody>
            <DrawerFooter>
              <FormSubmitButton className="w-full">Aplicar</FormSubmitButton>
            </DrawerFooter>
          </FormHandlerSubmit>
          <CpsButtonText onClick={resetFilterButton}>Limpar filtros</CpsButtonText>
        </DrawerContent>
      </FormRoot>
    </DrawerRoot>
  );
};
